import { Product } from '@shopware-pwa/types'
import { getProducts } from '@shopware-pwa/api-client'
import { getProductPermalink } from '~/lib'

export interface CustomFields {
  custom_product_fields_subtitle: string
  custom_product_fields_edition: string
  custom_product_fields_edition_statement: string
  custom_product_fields_binding: string
  custom_product_fields_weight: string
  custom_product_fields_page_count: string
  custom_product_fields_dimensions: string
  custom_product_fields_editor: string
  custom_product_fields_publisher: string
  custom_product_fields_product_type_number: string
  custom_product_fields_update_link: string
  custom_product_fields_other_text: string
}

export interface ProductVariation {
  id: string
  url: string
  price: string
  name: string
  type: string
}

export interface CrossSellingProduct {
  id: string
  order: number
  slug: string
  label: string
}

export interface CrossSelling {
  crossSelling: {
    type: string
  }
  products: Product[]
}

export const useCrossSellingProducts = () => {
  const { apiInstance } = useShopwareContext()
  const { getFormattedPrice } = usePrice()
  const { t } = useI18n()

  const PRODUCT_LANGUAGES = {
    ger: {
      label: t('shop.productPage.languageVariations.german'),
      order: 1,
    },
    fre: {
      label: t('shop.productPage.languageVariations.french'),
      order: 2,
    },
    ita: {
      label: t('shop.productPage.languageVariations.italian'),
      order: 3,
    },
    eng: {
      label: t('shop.productPage.languageVariations.english'),
      order: 4,
    },
  }

  const getCrossSelling = async (product: Product): Promise<CrossSelling[] | null> => {
    const resp = await apiInstance.invoke.post(`/store-api/product/${product.id}/cross-selling`, {
      associations: {
        properties: {},
        seoUrls: {},
      },
    })
    if (200 !== resp.status) {
      return null
    }
    return resp.data
  }

  const getVariants = async (product: Product, customFields: CustomFields) => {
    const { data: variants } = await useAsyncData('productVariants' + product.id, async () => {
      const response = await getProducts(
        {
          filter: [
            {
              type: 'equals',
              field: 'customFields.custom_product_fields_product_type_number',
              value: customFields.custom_product_fields_product_type_number,
            },
          ],
          associations: {
            properties: {},
            seoUrls: {},
          },
        },
        apiInstance,
      )

      const variants: ProductVariation[] = []

      response.elements.forEach((item) => {
        const property = item.properties?.find(
          (item) => item.customFields.custom_property_fields_type_property_group_code === 'product-type-group',
        )
        variants.push({
          id: item.id,
          url: getProductPermalink(item),
          price: getFormattedPrice(item.calculatedPrice.totalPrice),
          name: property?.name ?? '',
          type: (property?.customFields.custom_property_fields_type_property_code as string) ?? '',
        })
      })

      return variants
    })
    return variants
  }

  const getCrossSellingProducts = async (product: Product) => {
    const { data: crossSellingProducts } = await useAsyncData('productCrossSelling' + product.id, async () => {
      let languageVariations: CrossSellingProduct[] = []
      let relatedProducts: Array<Product> = []
      let relatedProductsReplaced: Array<Product> = []
      let productSetProducts: Array<Product> = []
      let containedProducts: Array<Product> = []
      const response = await getCrossSelling(product)

      if (!response) {
        return null
      }

      response.forEach((item: CrossSelling) => {
        if (item.crossSelling.type === 'language_related_products') {
          item.products.forEach((item: Product) => {
            let languageCode = item?.translated?.customFields?.custom_product_first_language_code as
              | keyof typeof PRODUCT_LANGUAGES
              | undefined
            if (languageCode) {
              languageVariations.push({
                id: item.id,
                order: PRODUCT_LANGUAGES[languageCode]?.order,
                slug: getProductPermalink(item),
                label: PRODUCT_LANGUAGES[languageCode]?.label,
              })
            }
          })
        } else if (item.crossSelling.type === 'product_sets') {
          productSetProducts = productSetProducts.concat(item.products)
        } else if (item.crossSelling.type === 'contained_products') {
          containedProducts = containedProducts.concat(item.products)
        } else if (item.crossSelling.type === 'related_products') {
          relatedProducts = relatedProducts.concat(item.products)
        } else if (item.crossSelling.type === 'related_products_replaced') {
          relatedProductsReplaced = relatedProductsReplaced.concat(item.products)
        }
      })
      // We also add the current product to the list.
      let languageCode = product.translated?.customFields
        ?.custom_product_first_language_code as keyof typeof PRODUCT_LANGUAGES
      if (languageCode) {
        languageVariations.push({
          id: product.id,
          order: PRODUCT_LANGUAGES[languageCode]?.order,
          slug: getProductPermalink(product),
          label: PRODUCT_LANGUAGES[languageCode]?.label,
        })
      }
      languageVariations.sort((a, b) => (a.order > b.order ? 1 : -1))

      return { languageVariations, relatedProducts, productSetProducts, containedProducts, relatedProductsReplaced }
    })

    return crossSellingProducts
  }
  return {
    getVariants,
    getCrossSellingProducts,
  }
}
